/**
 * Classic 2048 rules. No DOM — the web board (and a later client) have to play
 * by the same slide, merge-once, spawn, win, and stuck checks.
 */

export const GAME2048_SIZE = 4;
export const GAME2048_WIN = 2048;
export const GAME2048_SPAWN_FOUR_CHANCE = 0.1;
export const GAME2048_SAVE_VERSION = 1;
export const GAME2048_STORAGE_KEY = "ziolo.2048";

export type Game2048Dir = "up" | "down" | "left" | "right";
export type Game2048Rng = () => number;

export interface Game2048Tile {
  id: number;
  value: number;
  row: number;
  col: number;
  /** Sliding into a merge; drop after the animation. */
  merged: boolean;
  /** Spawned on the last move. */
  spawned: boolean;
  /** Created by a merge on the last move. */
  mergedResult: boolean;
}

export interface Game2048State {
  tiles: Game2048Tile[];
  score: number;
  won: boolean;
  wonAcknowledged: boolean;
  over: boolean;
  nextId: number;
  lastMoved: boolean;
}

export interface Game2048Save {
  version: number;
  tiles: Game2048Tile[];
  score: number;
  best: number;
  won: boolean;
  wonAcknowledged: boolean;
  over: boolean;
  nextId: number;
}

export interface Game2048Score {
  id: number;
  username: string;
  score: number;
  maxTile: number;
  createdAt: string;
}

const DIRS: Record<Game2048Dir, { row: number; col: number }[][]> = {
  left: linesAlong((r, i) => ({ row: r, col: i })),
  right: linesAlong((r, i) => ({ row: r, col: GAME2048_SIZE - 1 - i })),
  up: linesAlong((c, i) => ({ row: i, col: c })),
  down: linesAlong((c, i) => ({ row: GAME2048_SIZE - 1 - i, col: c })),
};

function linesAlong(
  at: (lane: number, i: number) => { row: number; col: number },
): { row: number; col: number }[][] {
  return Array.from({ length: GAME2048_SIZE }, (_, lane) =>
    Array.from({ length: GAME2048_SIZE }, (_, i) => at(lane, i)),
  );
}

function tile(
  id: number,
  value: number,
  row: number,
  col: number,
  flags?: Partial<Pick<Game2048Tile, "merged" | "spawned" | "mergedResult">>,
): Game2048Tile {
  return {
    id,
    value,
    row,
    col,
    merged: flags?.merged ?? false,
    spawned: flags?.spawned ?? false,
    mergedResult: flags?.mergedResult ?? false,
  };
}

export function livingTiles(tiles: readonly Game2048Tile[]): Game2048Tile[] {
  return tiles.filter((item) => !item.merged);
}

export function game2048Grid(state: Game2048State): number[][] {
  const grid = Array.from({ length: GAME2048_SIZE }, () =>
    Array.from({ length: GAME2048_SIZE }, () => 0),
  );
  for (const item of livingTiles(state.tiles)) {
    grid[item.row]![item.col] = item.value;
  }
  return grid;
}

function occupantGrid(tiles: readonly Game2048Tile[]): (Game2048Tile | null)[][] {
  const grid: (Game2048Tile | null)[][] = Array.from({ length: GAME2048_SIZE }, () =>
    Array.from({ length: GAME2048_SIZE }, () => null),
  );
  for (const item of livingTiles(tiles)) {
    grid[item.row]![item.col] = item;
  }
  return grid;
}

function emptyCells(tiles: readonly Game2048Tile[]): { row: number; col: number }[] {
  const taken = occupantGrid(tiles);
  const out: { row: number; col: number }[] = [];
  for (let row = 0; row < GAME2048_SIZE; row++) {
    for (let col = 0; col < GAME2048_SIZE; col++) {
      if (!taken[row]![col]) out.push({ row, col });
    }
  }
  return out;
}

function hasAdjacentEqual(tiles: readonly Game2048Tile[]): boolean {
  const grid = game2048Grid({
    tiles: [...tiles],
    score: 0,
    won: false,
    wonAcknowledged: false,
    over: false,
    nextId: 0,
    lastMoved: false,
  });
  for (let row = 0; row < GAME2048_SIZE; row++) {
    for (let col = 0; col < GAME2048_SIZE; col++) {
      const value = grid[row]![col]!;
      if (value === 0) continue;
      if (col + 1 < GAME2048_SIZE && grid[row]![col + 1] === value) return true;
      if (row + 1 < GAME2048_SIZE && grid[row + 1]![col] === value) return true;
    }
  }
  return false;
}

export function game2048HasMoves(state: Game2048State): boolean {
  const live = livingTiles(state.tiles);
  if (emptyCells(live).length > 0) return true;
  return hasAdjacentEqual(live);
}

export function pruneGame2048(state: Game2048State): Game2048State {
  return {
    ...state,
    tiles: livingTiles(state.tiles).map((item) =>
      tile(item.id, item.value, item.row, item.col),
    ),
  };
}

function finalize(state: Game2048State): Game2048State {
  const live = livingTiles(state.tiles);
  const won = state.won || live.some((item) => item.value >= GAME2048_WIN);
  const over = emptyCells(live).length === 0 && !hasAdjacentEqual(live);
  return { ...state, won, over };
}

function spawnTile(state: Game2048State, rng: Game2048Rng): Game2048State {
  const spots = emptyCells(state.tiles);
  if (spots.length === 0) return state;
  const pick = spots[Math.floor(rng() * spots.length)]!;
  const value = rng() < 1 - GAME2048_SPAWN_FOUR_CHANCE ? 2 : 4;
  return {
    ...state,
    nextId: state.nextId + 1,
    tiles: [...state.tiles, tile(state.nextId, value, pick.row, pick.col, { spawned: true })],
  };
}

export function spawnGame2048(state: Game2048State, rng: Game2048Rng): Game2048State {
  return finalize(spawnTile(state, rng));
}

export function newGame2048(rng: Game2048Rng = Math.random): Game2048State {
  const empty: Game2048State = {
    tiles: [],
    score: 0,
    won: false,
    wonAcknowledged: false,
    over: false,
    nextId: 1,
    lastMoved: false,
  };
  return pruneGame2048(finalize(spawnTile(spawnTile(empty, rng), rng)));
}

/**
 * Build a state from a 4×4 value grid. Used by tests (and debug setups).
 * Zero cells are empty. `nextId` continues after the placed tiles.
 */
export function game2048FromGrid(grid: number[][]): Game2048State {
  const tiles: Game2048Tile[] = [];
  let nextId = 1;
  for (let row = 0; row < GAME2048_SIZE; row++) {
    for (let col = 0; col < GAME2048_SIZE; col++) {
      const value = grid[row]?.[col] ?? 0;
      if (value <= 0) continue;
      tiles.push(tile(nextId, value, row, col));
      nextId += 1;
    }
  }
  return finalize({
    tiles,
    score: 0,
    won: false,
    wonAcknowledged: false,
    over: false,
    nextId,
    lastMoved: false,
  });
}

function slideLine(
  line: (Game2048Tile | null)[],
  positions: { row: number; col: number }[],
  nextId: { n: number },
): { tiles: Game2048Tile[]; score: number; moved: boolean } {
  const src = line.filter((item): item is Game2048Tile => item !== null);
  const tiles: Game2048Tile[] = [];
  let score = 0;
  let moved = false;
  let i = 0;
  let slot = 0;

  while (i < src.length) {
    const a = src[i]!;
    const b = src[i + 1];
    const dest = positions[slot]!;
    if (b && a.value === b.value) {
      const result = tile(nextId.n, a.value * 2, dest.row, dest.col, { mergedResult: true });
      nextId.n += 1;
      tiles.push(
        tile(a.id, a.value, dest.row, dest.col, { merged: true }),
        tile(b.id, b.value, dest.row, dest.col, { merged: true }),
        result,
      );
      score += result.value;
      moved = true;
      i += 2;
      slot += 1;
      continue;
    }
    tiles.push(tile(a.id, a.value, dest.row, dest.col));
    if (a.row !== dest.row || a.col !== dest.col) moved = true;
    i += 1;
    slot += 1;
  }

  return { tiles, score, moved };
}

export function moveGame2048(
  state: Game2048State,
  dir: Game2048Dir,
  rng: Game2048Rng = Math.random,
): Game2048State {
  if (state.over) return { ...state, lastMoved: false };

  const base = pruneGame2048(state);
  const grid = occupantGrid(base.tiles);
  const nextId = { n: base.nextId };
  const tiles: Game2048Tile[] = [];
  let score = 0;
  let moved = false;

  for (const positions of DIRS[dir]) {
    const line = positions.map((pos) => grid[pos.row]![pos.col]!);
    const result = slideLine(line, positions, nextId);
    tiles.push(...result.tiles);
    score += result.score;
    moved = moved || result.moved;
  }

  if (!moved) return { ...base, lastMoved: false };

  const slid: Game2048State = {
    ...base,
    tiles,
    score: base.score + score,
    nextId: nextId.n,
    lastMoved: true,
  };
  return finalize(spawnTile(slid, rng));
}

export function acknowledgeGame2048Win(state: Game2048State): Game2048State {
  return { ...state, wonAcknowledged: true };
}

export function emptyGame2048Save(best = 0): Game2048Save {
  return {
    version: GAME2048_SAVE_VERSION,
    tiles: [],
    score: 0,
    best: Math.max(0, Math.floor(best)),
    won: false,
    wonAcknowledged: false,
    over: false,
    nextId: 1,
  };
}

function asInt(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

function isPowerOfTwo(value: number): boolean {
  return value >= 2 && value <= 1_048_576 && (value & (value - 1)) === 0;
}

function parseTile(raw: unknown): Game2048Tile | null {
  if (typeof raw !== "object" || raw === null) return null;
  const data = raw as Record<string, unknown>;
  const id = asInt(data.id, 0);
  const value = asInt(data.value, 0);
  const row = asInt(data.row, -1);
  const col = asInt(data.col, -1);
  if (id <= 0 || !isPowerOfTwo(value)) return null;
  if (row < 0 || row >= GAME2048_SIZE || col < 0 || col >= GAME2048_SIZE) return null;
  return tile(id, value, row, col);
}

export function parseGame2048Save(raw: unknown): Game2048Save {
  const empty = emptyGame2048Save();
  if (typeof raw !== "object" || raw === null) return empty;
  const data = raw as Record<string, unknown>;
  const tilesRaw = Array.isArray(data.tiles) ? data.tiles : [];
  const seen = new Set<string>();
  const tiles: Game2048Tile[] = [];
  let maxId = 0;
  for (const item of tilesRaw) {
    const parsed = parseTile(item);
    if (!parsed) continue;
    const key = `${parsed.row},${parsed.col}`;
    if (seen.has(key)) continue;
    seen.add(key);
    tiles.push(parsed);
    if (parsed.id > maxId) maxId = parsed.id;
  }
  const score = Math.max(0, asInt(data.score, 0));
  const best = Math.max(score, asInt(data.best, 0));
  const nextId = Math.max(maxId + 1, asInt(data.nextId, 1), 1);
  const draft: Game2048State = {
    tiles,
    score,
    won: Boolean(data.won),
    wonAcknowledged: Boolean(data.wonAcknowledged),
    over: false,
    nextId,
    lastMoved: false,
  };
  const final = finalize(draft);
  return {
    version: GAME2048_SAVE_VERSION,
    tiles: final.tiles,
    score: final.score,
    best,
    won: final.won,
    wonAcknowledged: final.won ? Boolean(data.wonAcknowledged) : false,
    over: final.over,
    nextId: final.nextId,
  };
}

export function game2048StateFromSave(save: Game2048Save): Game2048State {
  return {
    tiles: save.tiles.map((item) => tile(item.id, item.value, item.row, item.col)),
    score: save.score,
    won: save.won,
    wonAcknowledged: save.wonAcknowledged,
    over: save.over,
    nextId: save.nextId,
    lastMoved: false,
  };
}

export function game2048SaveFromState(state: Game2048State, best: number): Game2048Save {
  const pruned = pruneGame2048(state);
  return {
    version: GAME2048_SAVE_VERSION,
    tiles: pruned.tiles,
    score: pruned.score,
    best: Math.max(best, pruned.score),
    won: pruned.won,
    wonAcknowledged: pruned.wonAcknowledged,
    over: pruned.over,
    nextId: pruned.nextId,
  };
}

export function keyToGame2048Dir(key: string): Game2048Dir | null {
  switch (key) {
    case "ArrowLeft":
    case "a":
    case "A":
      return "left";
    case "ArrowRight":
    case "d":
    case "D":
      return "right";
    case "ArrowUp":
    case "w":
    case "W":
      return "up";
    case "ArrowDown":
    case "s":
    case "S":
      return "down";
    default:
      return null;
  }
}

export function swipeToGame2048Dir(dx: number, dy: number, min = 24): Game2048Dir | null {
  if (Math.abs(dx) < min && Math.abs(dy) < min) return null;
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? "right" : "left";
  return dy > 0 ? "down" : "up";
}

export function game2048MaxTile(state: Game2048State): number {
  let max = 0;
  for (const item of livingTiles(state.tiles)) {
    if (item.value > max) max = item.value;
  }
  return max;
}

export function game2048Cells(state: Game2048State): number[] {
  return game2048Grid(state).flat();
}

export function parseGame2048Cells(value: unknown): number[] | null {
  if (!Array.isArray(value) || value.length !== GAME2048_SIZE * GAME2048_SIZE) return null;
  const out: number[] = [];
  for (const item of value) {
    if (typeof item !== "number" || !Number.isInteger(item) || item < 0) return null;
    if (item !== 0 && !isPowerOfTwo(item)) return null;
    if (item > 1_048_576) return null;
    out.push(item);
  }
  return out;
}

export function game2048StateFromCells(cells: number[]): Game2048State {
  const grid: number[][] = [];
  for (let row = 0; row < GAME2048_SIZE; row++) {
    grid.push(cells.slice(row * GAME2048_SIZE, (row + 1) * GAME2048_SIZE));
  }
  return game2048FromGrid(grid);
}

function maxPossibleScore(maxTile: number): number {
  if (maxTile < 4) return 0;
  const log = Math.log2(maxTile);
  return Math.floor(maxTile * (log + 8));
}

/**
 * Rejects a submitted run the server cannot confirm from the board snapshot.
 * Replay stays client-side; ranking still checks occupancy, stuck/win, and bounds.
 */
export function game2048ScoreRejection(input: {
  score: number;
  maxTile: number;
  cells: readonly number[];
}): string | null {
  const cells = parseGame2048Cells([...input.cells]);
  if (!cells) return "Nieprawidłowa plansza";
  const state = game2048StateFromCells(cells);
  const maxTile = game2048MaxTile(state);
  if (maxTile !== input.maxTile) return "Kafel nie pasuje do planszy";
  if (!state.over && !state.won) return "Gra jeszcze trwa";
  if (input.score < 4) return "Wynik jest za niski";
  if (input.score > maxPossibleScore(maxTile)) return "Nieprawidłowy wynik";
  return null;
}
